/**
 * «Alertas de gasto de Twilio» (Usage Triggers): gasto diario, gasto mensual y saldo mínimo.
 * Vacío = sin alerta. Twilio cuenta el día y el mes en horario UTC (F6): se aclara siempre,
 * porque «el gasto de hoy» de Twilio empieza a las 21:00 de Argentina.
 *
 * Guarda por su endpoint (`PUT /estadisticas/twilio/alertas`), no por el PATCH de la config: el
 * backend rechaza `alertasTwilio` en el PATCH para que los triggers de Twilio se sincronicen
 * siempre que cambian los montos.
 */

import { useCallback, useEffect, useId, useState } from "react";
import type { FormEvent } from "react";
import { CheckCircle2, Loader2, Zap } from "lucide-react";
import type { AlertasTwilio, ClaveTrigger } from "../../types.estadisticas";
import { estadisticasApi } from "../../estadisticasApi";
import { leerError, leerRespuesta, textoError } from "../../logic/respuesta";
import { ACLARACION_UTC, formularioAlertas, validarAlertasGasto } from "../../logic/estadisticas";
import { Tarjeta } from "../Tarjeta";
import { ListaTriggers } from "./TarjetaCuentaTwilio";
import { ErrorTarjeta, Esqueleto } from "./comun";

type Campo = "gastoDiario" | "gastoMensual" | "saldoMinimo";
const CAMPOS: { clave: Campo; label: string; ayuda: string }[] = [
  { clave: "gastoDiario", label: "Gasto del día mayor a", ayuda: "Día en horario UTC." },
  { clave: "gastoMensual", label: "Gasto del mes mayor a", ayuda: "Mes en horario UTC." },
  { clave: "saldoMinimo", label: "Saldo de Twilio menor a", ayuda: "Sólo con la cuenta principal." },
];

export function TarjetaAlertasGasto({ simulado = false }: { simulado?: boolean }) {
  const [alertas, setAlertas] = useState<AlertasTwilio | null>(null);
  const [errorCarga, setErrorCarga] = useState("");
  const [cargando, setCargando] = useState(true);
  const [form, setForm] = useState<Record<Campo, string>>(formularioAlertas(null));
  const [errores, setErrores] = useState<Partial<Record<Campo, string>>>({});
  const [ocupado, setOcupado] = useState<"" | "guardar" | ClaveTrigger>("");
  const [ok, setOk] = useState("");
  const [error, setError] = useState("");
  const base = useId();

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const l = leerRespuesta<AlertasTwilio>(await estadisticasApi.alertas());
      if (!l.ok) { setErrorCarga(textoError(l)); return; }
      if (!l.data) { setErrorCarga("El servidor no devolvió las alertas."); return; }
      setAlertas(l.data);
      setForm(formularioAlertas(l.data.config));
      setErrorCarga("");
    } catch (e) {
      setErrorCarga(textoError(leerError(e)));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { void cargar(); }, [cargar]);

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setOk("");
    setError("");
    const v = validarAlertasGasto(form);
    if (!v.ok) { setErrores(v.errores); return; }
    setErrores({});
    setOcupado("guardar");
    try {
      const l = leerRespuesta<AlertasTwilio>(await estadisticasApi.guardarAlertas(v.config));
      if (!l.ok) { setError(textoError(l)); return; }
      if (l.data) { setAlertas(l.data); setForm(formularioAlertas(l.data.config)); }
      setOk("Alertas guardadas y avisadas a Twilio.");
    } catch (err) {
      setError(textoError(leerError(err)));
    } finally {
      setOcupado("");
    }
  };

  const simular = async (clave: ClaveTrigger) => {
    setOk("");
    setError("");
    setOcupado(clave);
    try {
      const l = leerRespuesta<undefined>(await estadisticasApi.simularDisparo(clave));
      if (!l.ok) { setError(textoError(l)); return; }
      setOk("Disparo simulado: la alerta aparece en el Resumen.");
      await cargar();
    } catch (err) {
      setError(textoError(leerError(err)));
    } finally {
      setOcupado("");
    }
  };

  return (
    <Tarjeta titulo="Alertas de gasto de Twilio" sub={ACLARACION_UTC}>
      {!alertas ? (
        cargando ? <Esqueleto alto="h-24" /> : <ErrorTarjeta texto={`No pudimos traer las alertas. ${errorCarga}`} onReintentar={() => void cargar()} />
      ) : (
        <div className="space-y-5">
          <form onSubmit={guardar} className="space-y-4" noValidate>
            <div className="grid sm:grid-cols-3 gap-4">
              {CAMPOS.map((c) => (
                <div key={c.clave}>
                  <label htmlFor={`${base}-${c.clave}`} className="text-sm font-medium text-slate-700">{c.label}</label>
                  <div className="mt-1 flex items-center gap-2">
                    <input id={`${base}-${c.clave}`} inputMode="decimal" value={form[c.clave]} placeholder="Sin alerta"
                      onChange={(ev) => setForm((f) => ({ ...f, [c.clave]: ev.target.value.slice(0, 12) }))}
                      aria-invalid={!!errores[c.clave]} aria-describedby={`${base}-${c.clave}-ayuda`}
                      className={`w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${errores[c.clave] ? "border-red-300" : "border-slate-200"}`} />
                    <span className="text-xs text-slate-500">USD</span>
                  </div>
                  <p id={`${base}-${c.clave}-ayuda`} className={`text-xs mt-1 ${errores[c.clave] ? "text-red-600" : "text-slate-400"}`}>{errores[c.clave] || c.ayuda}</p>
                </div>
              ))}
            </div>
            {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
            {ok && <p role="status" className="text-sm text-emerald-700 inline-flex items-center gap-1.5"><CheckCircle2 size={15} /> {ok}</p>}
            <div className="flex items-center gap-2 flex-wrap">
              <button type="submit" disabled={!!ocupado}
                className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-40">
                {ocupado === "guardar" && <Loader2 size={15} className="animate-spin" />} Guardar
              </button>
              {simulado && (["gasto_diario", "gasto_mensual"] as ClaveTrigger[]).map((k) => (
                <button key={k} type="button" onClick={() => void simular(k)} disabled={!!ocupado}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-40">
                  {ocupado === k ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />} Simular disparo ({k === "gasto_diario" ? "diario" : "mensual"})
                </button>
              ))}
            </div>
          </form>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">En Twilio</h4>
            <ListaTriggers triggers={alertas.triggers} />
          </div>
        </div>
      )}
    </Tarjeta>
  );
}

export default TarjetaAlertasGasto;
